import axios from "axios";

// ============= EMAIL TEMPLATES =============

export const getEmailTemplates = () => async (dispatch) => {
  const res = await axios.get("/api/email/templates");
  return res.data;
};

export const getEmailTemplate = (id) => async (dispatch) => {
  const res = await axios.get(`/api/email/templates/${id}`);
  return res.data;
};

export const createEmailTemplate = (template) => async (dispatch) => {
  const res = await axios.post("/api/email/templates", template);
  return res.data;
};

export const updateEmailTemplate = (id, template) => async (dispatch) => {
  const res = await axios.put(`/api/email/templates/${id}`, template);
  return res.data;
};

export const deleteEmailTemplate = (id) => async (dispatch) => {
  const res = await axios.delete(`/api/email/templates/${id}`);
  return res.data;
};

export const previewEmailTemplate = (id, customerId) => async (dispatch) => {
  const res = await axios.post(`/api/email/templates/${id}/preview`, { customerId });
  return res.data;
};

// ============= EMAIL SEQUENCES =============

export const getEmailSequences = () => async (dispatch) => {
  const res = await axios.get("/api/email/sequences");
  return res.data;
};

export const getEmailSequence = (id) => async (dispatch) => {
  const res = await axios.get(`/api/email/sequences/${id}`);
  return res.data;
};

export const createEmailSequence = (sequence) => async (dispatch) => {
  const res = await axios.post("/api/email/sequences", sequence);
  return res.data;
};

export const updateEmailSequence = (id, sequence) => async (dispatch) => {
  const res = await axios.put(`/api/email/sequences/${id}`, sequence);
  return res.data;
};

export const deleteEmailSequence = (id) => async (dispatch) => {
  const res = await axios.delete(`/api/email/sequences/${id}`);
  return res.data;
};

// Turn a sequence on. includeExisting: also enroll customers who ALREADY have a trigger tag.
// → { sequence, enrolledExisting }
export const activateEmailSequence = (id, includeExisting = false) => async (dispatch) => {
  const res = await axios.post(`/api/email/sequences/${id}/activate`, { includeExisting });
  return res.data;
};

// Turn off: everyone in the middle is frozen in place and continues when turned back on. → { sequence }
export const deactivateEmailSequence = (id) => async (dispatch) => {
  const res = await axios.post(`/api/email/sequences/${id}/deactivate`);
  return res.data;
};

// What an action WILL do, without doing it (dry run). Shown to the admin before confirming.
// body { action: 'create'|'save'|'activate'|'deactivate'|'delete', sequenceId?, draft? }
// → see services/service.emailSequences.js impact()
export const getSequenceImpact = (body) => async (dispatch) => {
  const res = await axios.post(`/api/email/sequences/impact`, body);
  return res.data;
};

// ============= SEQUENCE STEPS / TAGS (old single-item endpoints — not used by the current screens) =============

export const addSequenceStep = (sequenceId, step) => async (dispatch) => {
  const res = await axios.post(`/api/email/sequences/${sequenceId}/steps`, step);
  return res.data;
};

export const updateSequenceStep = (sequenceId, stepId, step) => async (dispatch) => {
  const res = await axios.put(`/api/email/sequences/${sequenceId}/steps/${stepId}`, step);
  return res.data;
};

export const deleteSequenceStep = (sequenceId, stepId) => async (dispatch) => {
  const res = await axios.delete(`/api/email/sequences/${sequenceId}/steps/${stepId}`);
  return res.data;
};

// ============= SEQUENCE TAGS =============

export const addSequenceTag = (sequenceId, tagId) => async (dispatch) => {
  const res = await axios.post(`/api/email/sequences/${sequenceId}/tags`, { tag_id: tagId });
  return res.data;
};

export const removeSequenceTag = (sequenceId, tagId) => async (dispatch) => {
  const res = await axios.delete(`/api/email/sequences/${sequenceId}/tags/${tagId}`);
  return res.data;
};

// ============= ENROLLMENTS =============

export const getCustomerEnrollments = (customerId) => async (dispatch) => {
  const res = await axios.get(`/api/email/enrollments/customer/${customerId}`);
  return res.data;
};

export const getSequenceEnrollments = (sequenceId, status) => async (dispatch) => {
  const url = status
    ? `/api/email/enrollments/sequence/${sequenceId}?status=${status}`
    : `/api/email/enrollments/sequence/${sequenceId}`;
  const res = await axios.get(url);
  return res.data;
};

// ============= SCHEDULER =============

export const getSchedulerStatus = () => async (dispatch) => {
  const res = await axios.get("/api/email/scheduler/status");
  return res.data;
};

export const getSchedulerStats = () => async (dispatch) => {
  const res = await axios.get("/api/email/scheduler/stats");
  return res.data;
};

// ============= DASHBOARD =============

export const getDashboardStats = () => async (dispatch) => {
  const res = await axios.get("/api/email/dashboard");
  return res.data;
};

export const getEmailHealth = () => async (dispatch) => {
  const res = await axios.get("/api/email/health");
  return res.data;
};

export const getQueueJobs = (filters = "") => async (dispatch) => {
  const res = await axios.get(`/api/email/queue/jobs${filters}`);
  return res.data;
};

export const cancelQueueJob = (jobId) => async (dispatch) => {
  const res = await axios.post(`/api/email/queue/jobs/${jobId}/cancel`);
  return res.data;
};

export const getAllEnrollments = (filters = "") => async (dispatch) => {
  const res = await axios.get(`/api/email/enrollments${filters}`);
  return res.data;
};

export const pauseEnrollment = (enrollmentId) => async (dispatch) => {
  const res = await axios.post(`/api/email/enrollments/${enrollmentId}/pause`);
  return res.data;
};

export const resumeEnrollment = (enrollmentId) => async (dispatch) => {
  const res = await axios.post(`/api/email/enrollments/${enrollmentId}/resume`);
  return res.data;
};

// ============= EMAIL ATTACHMENTS =============

export const getAttachmentLimits = () => async (dispatch) => {
  const res = await axios.get("/api/email/attachments/limits");
  return res.data;
};

export const uploadAttachment = (file, templateId = null) => async (dispatch) => {
  const formData = new FormData();
  formData.append('file', file);

  const url = templateId
    ? `/api/email/attachments/upload?templateId=${templateId}`
    : '/api/email/attachments/upload';

  const res = await axios.post(url, formData);
  return res.data;
};

export const uploadTemplateAttachment = (templateId, file) => async (dispatch) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await axios.post(`/api/email/templates/${templateId}/attachments`, formData);
  return res.data;
};

export const deleteAttachment = (id) => async (dispatch) => {
  const res = await axios.delete(`/api/email/attachments/${id}`);
  return res.data;
};

export const deleteTemplateAttachment = (templateId, attachmentId) => async (dispatch) => {
  const res = await axios.delete(`/api/email/templates/${templateId}/attachments/${attachmentId}`);
  return res.data;
};

export const getTemplateAttachments = (templateId) => async (dispatch) => {
  const res = await axios.get(`/api/email/templates/${templateId}/attachments`);
  return res.data;
};

export const validateAttachments = (attachmentIds) => async (dispatch) => {
  const res = await axios.post("/api/email/attachments/validate", { attachmentIds });
  return res.data;
};
